import { GmailThread } from "../../lib/gmail";

/**
 * Sender identity for the inbox: how threads are grouped into senders,
 * and how a sender's avatar is derived from its email domain.
 */

export interface SenderGroup {
  key: string;
  name: string;
  /** First email address encountered for this sender — used to derive
   *  the domain for the favicon avatar. May be empty if unavailable. */
  email: string;
  count: number;
}

function threadTime(thread: GmailThread): number {
  return new Date(thread.date).getTime() || 0;
}

/**
 * Group threads by their latest sender. The latest message in each
 * thread is the conversation's "from" — for inbox-only threads this
 * is reliably the other party (your sent replies live under the SENT
 * label, not INBOX). Counts represent threads, not raw messages, so
 * a 27-message conversation collapses to "1" in the sender tile.
 *
 * Senders are listed by their newest conversation, most recent first.
 * A sender with several conversations uses the newest of those, not
 * how many they sent. A tie keeps name order.
 */
export function groupThreadsBySender(threads: GmailThread[]): SenderGroup[] {
  const groups = new Map<string, SenderGroup>();
  const latestAt = new Map<string, number>();

  for (const thread of threads) {
    const key = senderKey(thread);
    const at = threadTime(thread);
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      latestAt.set(key, Math.max(latestAt.get(key) ?? 0, at));
    } else {
      groups.set(key, {
        key,
        name: (thread.fromName || thread.from || "Unknown").trim(),
        email: thread.from || "",
        count: 1,
      });
      latestAt.set(key, at);
    }
  }

  return Array.from(groups.values()).sort((a, b) => {
    const byDate = (latestAt.get(b.key) ?? 0) - (latestAt.get(a.key) ?? 0);
    if (byDate !== 0) return byDate;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Grouping key for a thread's sender — the email address, lowercased.
 * Keying on the address (not the display name) keeps two different
 * accounts that happen to share a display name — e.g. two "Aviv Hadar"
 * inboxes — as separate senders. Falls back to the display name, then
 * "Unknown", only for the rare message with no parseable From address.
 */
export function senderKey(thread: GmailThread): string {
  return (thread.from || thread.fromName || "Unknown").trim().toLowerCase();
}

export function getInitial(name: string): string {
  const firstChar = name.trim().charAt(0);
  return firstChar ? firstChar.toUpperCase() : "?";
}

export function getDomainFromEmail(email: string): string | null {
  const at = email.lastIndexOf("@");
  if (at === -1 || at === email.length - 1) return null;
  const domain = email.slice(at + 1).trim().toLowerCase();
  return domain || null;
}

export function getDirectFaviconUrl(domain: string): string {
  return `https://${domain}/favicon.ico`;
}

export function getGoogleFaviconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`;
}

export function getBrandDomain(domain: string): string | null {
  const parts = domain.split(".");
  if (parts.length > 2) {
    return parts.slice(1).join(".");
  }
  const suffixes = ["mail", "email", "notify", "notifications", "news", "info"];
  const [base, ...tld] = parts;
  if (tld.length > 0) {
    for (const suffix of suffixes) {
      if (base.endsWith(suffix) && base.length > suffix.length) {
        return base.slice(0, -suffix.length) + "." + tld.join(".");
      }
    }
  }
  return null;
}
